import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Bell, BellOff, X } from 'lucide-react';
import { usePushNotification } from '../hooks/usePushNotification';

/**
 * Banner prompt component to encourage users to enable notifications
 * Only shows for authenticated users
 */
export const PushNotificationBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const { isSubscribed, isLoading, permissionStatus, isAuthenticated, subscribe } = usePushNotification();

  useEffect(() => {
    // Only show banner for authenticated users
    if (!isAuthenticated) {
      return;
    }

    // Show banner if not subscribed and permission not denied
    if (!isSubscribed && permissionStatus !== 'denied' && permissionStatus !== 'unsupported') {
      // Check if user has dismissed the banner before
      const dismissed = localStorage.getItem('notification_banner_dismissed');
      const dismissedTime = dismissed ? parseInt(dismissed) : 0;
      const now = Date.now();
      
      // Show banner again after 7 days or if never dismissed
      if (!dismissed || (now - dismissedTime) > 7 * 24 * 60 * 60 * 1000) {
        setTimeout(() => setShowBanner(true), 3000);
      }
    }
  }, [isSubscribed, permissionStatus, isAuthenticated]);

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('notification_banner_dismissed', Date.now().toString());
  };

  const handleEnable = async () => {
    const success = await subscribe();
    if (success) {
      setShowBanner(false);
    }
  };

  if (!showBanner || isSubscribed) {
    return null;
  }

  return (
    <div className="hidden md:block fixed top-0 left-0 right-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 shadow-lg z-50 animate-slide-down">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center space-x-3 flex-1">
          <Bell className="w-6 h-6 flex-shrink-0" />
          <div>
            <p className="font-semibold">Stay Updated!</p>
            <p className="text-sm opacity-90">
              Get instant notifications about new match predictions
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          <button
            onClick={handleEnable}
            disabled={isLoading}
            className="bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition disabled:opacity-50 whitespace-nowrap"
          >
            {isLoading ? 'Enabling...' : 'Enable Notifications'}
          </button>
          <button
            onClick={handleDismiss}
            className="p-2 hover:bg-white/10 rounded-lg transition"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Toggle component for settings/profile pages
 * Only available for authenticated users
 */
export const PushNotificationToggle = ({ className = '' }) => {
  const { isSubscribed, isLoading, permissionStatus, isAuthenticated, toggleSubscription } = usePushNotification();

  const getStatusText = () => {
    if (!isAuthenticated) {
      return 'You must be logged in to enable notifications';
    }
    if (permissionStatus === 'denied') {
      return 'Notifications blocked. Enable in browser settings.';
    }
    if (permissionStatus === 'unsupported') {
      return 'Push notifications not supported in this browser.';
    }
    if (isSubscribed) {
      return 'You will receive notifications about new predictions';
    }
    return 'Enable notifications to stay updated';
  };

  const isDisabled = !isAuthenticated || permissionStatus === 'denied' || permissionStatus === 'unsupported' || isLoading;

  return (
    <div className={`hidden md:flex items-center justify-between p-4 bg-white rounded-lg shadow ${className}`}>
      <div className="flex items-center space-x-3 flex-1">
        {isSubscribed ? (
          <Bell className="w-5 h-5 text-green-600 flex-shrink-0" />
        ) : (
          <BellOff className="w-5 h-5 text-gray-400 flex-shrink-0" />
        )}
        <div className="flex-1">
          <p className="font-medium text-gray-800">Push Notifications</p>
          <p className="text-sm text-gray-500">
            {getStatusText()}
          </p>
        </div>
      </div>
      <button
        onClick={toggleSubscription}
        disabled={isDisabled}
        className={`px-4 py-2 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ${
          isSubscribed
            ? 'bg-red-100 text-red-600 hover:bg-red-200'
            : 'bg-purple-600 text-white hover:bg-purple-700'
        }`}
      >
        {isLoading ? 'Processing...' : isSubscribed ? 'Disable' : 'Enable'}
      </button>
    </div>
  );
};

PushNotificationToggle.propTypes = {
  className: PropTypes.string
};

/**
 * Compact toggle for mobile navigation or quick settings
 * Only available for authenticated users
 */
export const PushNotificationCompactToggle = () => {
  const { isSubscribed, isLoading, isAuthenticated, toggleSubscription } = usePushNotification();

  // Don't show if not authenticated or on mobile
  if (!isAuthenticated) {
    return null;
  }

  return (
    <button
      onClick={toggleSubscription}
      disabled={isLoading}
      className={`hidden md:flex items-center space-x-2 p-2 rounded-lg transition disabled:opacity-50 ${
        isSubscribed
          ? 'bg-green-100 text-green-700'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
      title={isSubscribed ? 'Disable notifications' : 'Enable notifications'}
    >
      {isSubscribed ? (
        <Bell className="w-4 h-4" />
      ) : (
        <BellOff className="w-4 h-4" />
      )}
      <span className="text-sm font-medium">
        {isLoading ? 'Loading...' : isSubscribed ? 'Notifications On' : 'Notifications Off'}
      </span>
    </button>
  );
};

export default PushNotificationToggle;

